import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Users, Shield, Plus, Edit, Trash2, Eye, EyeOff, Save,
  BarChart3, Check, X, User, Building, Tag, Paperclip, FolderOpen,
  Calendar, CreditCard, Clock, FileText, History, Zap, UserPlus,
  Briefcase, DollarSign, Receipt, Percent, Layers, List, ChevronRight,
  ChevronLeft, Loader2, AlertCircle, RefreshCw, Bot, Mail, Image,
  HardDrive, Download, Database, Cloud, ExternalLink
} from 'lucide-react';
import AISettings from '../components/AISettings';
import AIExpedienteSettings from '../components/AIExpedienteSettings';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { usuariosAPI, configuracionAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Definición de todas las opciones de configuración
const CATEGORIAS_CONFIG = [
  {
    id: 'general',
    nombre: 'Configuración General',
    opciones: [
      { id: 'logo', nombre: 'Logo de la aplicación', icon: Image, descripcion: 'Personaliza el logo del sistema', tipo: 'link', link: '/configuracion/logo' },
      { id: 'cuentas-email', nombre: 'Cuentas de Email', icon: Mail, descripcion: 'Configura cuentas SMTP/IMAP/POP para enviar comunicaciones', tipo: 'link', link: '/configuracion/cuentas-email' },
      { id: 'accesos-rapidos', nombre: 'Accesos rápidos', icon: Zap, descripcion: 'Configura los accesos directos del panel', tipo: 'especial' },
      { id: 'gestion-contactos', nombre: 'Gestión de contactos', icon: Users, descripcion: 'Tipos y subtipos de contactos', tipo: 'items' },
      { id: 'tipos-adjunto', nombre: 'Tipos de adjunto', icon: Paperclip, descripcion: 'Categorías de archivos adjuntos', tipo: 'items' },
      { id: 'etiquetas', nombre: 'Etiquetas', icon: Tag, descripcion: 'Gestión de etiquetas del sistema', tipo: 'items', conColor: true },
      { id: 'gestion-empresa', nombre: 'Gestión de empresa', icon: Building, descripcion: 'Datos del despacho', tipo: 'empresa' },
      { id: 'campos-personalizados', nombre: 'Campos personalizados', icon: List, descripcion: 'Campos adicionales para formularios', tipo: 'campos' },
    ]
  },
  {
    id: 'expedientes',
    nombre: 'Expedientes Legales',
    opciones: [
      { id: 'tipos-expediente', nombre: 'Tipos de expediente legal', icon: FolderOpen, descripcion: 'Clasificación de expedientes', tipo: 'items' },
      { id: 'clases-expediente', nombre: 'Clases de expediente legal', icon: Layers, descripcion: 'Clases y categorías', tipo: 'items' },
      { id: 'series-expediente', nombre: 'Series de expediente', icon: List, descripcion: 'Series de numeración', tipo: 'items' },
      { id: 'fases-expediente', nombre: 'Fases de expediente legal', icon: Briefcase, descripcion: 'Etapas del proceso', tipo: 'items' },
      { id: 'ajustes-informes', nombre: 'Ajustes de informes', icon: FileText, descripcion: 'Configuración de informes', tipo: 'items' },
      { id: 'ia-expedientes', nombre: 'IA de Expedientes', icon: Bot, descripcion: 'Configuración de IA para análisis de expedientes', tipo: 'ai-expedientes' },
    ]
  },
  {
    id: 'eventos',
    nombre: 'Eventos',
    opciones: [
      { id: 'gestion-calendarios', nombre: 'Gestión de calendarios', icon: Calendar, descripcion: 'Calendarios del despacho', tipo: 'calendarios' },
      { id: 'categorias-calendario', nombre: 'Categorías de calendario', icon: Calendar, descripcion: 'Tipos de eventos (Juicio, Audiencia, etc.)', tipo: 'link', link: '/configuracion/categorias-calendario' },
      { id: 'tipos-eventos', nombre: 'Tipos de eventos', icon: Calendar, descripcion: 'Categorías de eventos', tipo: 'items', conColor: true },
    ]
  },
  {
    id: 'contabilidad',
    nombre: 'Contabilidad',
    opciones: [
      { id: 'series-facturacion', nombre: 'Series de facturación', icon: Receipt, descripcion: 'Series para numeración de facturas', tipo: 'items' },
      { id: 'tipos-conceptos', nombre: 'Tipos de conceptos facturables', icon: List, descripcion: 'Categorías de conceptos', tipo: 'items' },
      { id: 'conceptos-precargados', nombre: 'Conceptos facturables precargados', icon: FileText, descripcion: 'Conceptos predefinidos', tipo: 'items' },
      { id: 'tipos-impuestos', nombre: 'Tipos de impuestos', icon: Percent, descripcion: 'Configuración de IVA y otros', tipo: 'items', conValor: true },
      { id: 'tipos-gastos', nombre: 'Tipos de gastos', icon: DollarSign, descripcion: 'Categorías de gastos', tipo: 'items' },
      { id: 'formas-pago', nombre: 'Formas de pago', icon: CreditCard, descripcion: 'Métodos de pago', tipo: 'items' },
      { id: 'facturadores', nombre: 'Facturadores', icon: User, descripcion: 'Responsables de facturación', tipo: 'facturadores' },
      { id: 'tipos-precio-hora', nombre: 'Tipos precio/hora', icon: Clock, descripcion: 'Tarifas horarias', tipo: 'items', conValor: true },
      { id: 'tipos-presentacion', nombre: 'Tipos de presentación', icon: FileText, descripcion: 'Formatos de presentación', tipo: 'items' },
    ]
  },
  {
    id: 'extrajudicial',
    nombre: 'Reclamaciones Extrajudiciales',
    opciones: [
      { id: 'catalogo-clausulas', nombre: 'Catálogo de Cláusulas', icon: List, descripcion: 'Gestionar cláusulas abusivas disponibles', tipo: 'link', link: '/configuracion/catalogo-clausulas' },
    ]
  },
  {
    id: 'estafas-bancarias',
    nombre: 'Estafas Bancarias',
    opciones: [
      { id: 'plantillas-estafas', nombre: 'Plantillas de Documentos', icon: FileText, descripcion: 'Plantillas Word para Reclamación (D), Hoja de Encargo (E) e Informe (F)', tipo: 'link', link: '/configuracion/plantillas-estafas' },
    ]
  },
  {
    id: 'atencion-cliente',
    nombre: 'Atención al Cliente',
    opciones: [
      { id: 'config-atencion', nombre: 'Configuración Email y Plantillas', icon: Mail, descripcion: 'SMTP y plantillas de respuesta', tipo: 'link', link: '/configuracion/atencion-cliente' },
      { id: 'portal-cliente', nombre: 'Portal del Cliente', icon: Users, descripcion: 'Gestionar accesos de clientes al portal', tipo: 'link', link: '/configuracion/portal-cliente' },
    ]
  },
  {
    id: 'modulo-correo',
    nombre: 'Módulo de Correo',
    opciones: [
      { id: 'cuentas-correo', nombre: 'Cuentas de Correo Gmail', icon: Mail, descripcion: 'Configurar cuentas de Gmail para gestión de correo', tipo: 'link', link: '/configuracion/cuentas-correo' },
    ]
  },
  {
    id: 'ajustes-generales',
    nombre: 'Ajustes Generales',
    opciones: [
      { id: 'usuarios', nombre: 'Usuarios', icon: User, descripcion: 'Gestión de usuarios del sistema', tipo: 'usuarios' },
      { id: 'grupos-usuarios', nombre: 'Grupos de usuarios', icon: Users, descripcion: 'Roles y permisos', tipo: 'grupos' },
      { id: 'accesos-rapidos', nombre: 'Accesos Rápidos', icon: Zap, descripcion: 'Configura los accesos directos del botón "+" en expedientes', tipo: 'accesos-rapidos' },
      { id: 'configuracion-email', nombre: 'Configuración de Email', icon: Settings, descripcion: 'Servidor SMTP para alertas', tipo: 'link', link: '/configuracion/email' },
      { id: 'historial-acciones', nombre: 'Historial de acciones', icon: History, descripcion: 'Registro de actividad', tipo: 'historial' },
      { id: 'asistente-ia', nombre: 'Asistente IA', icon: Bot, descripcion: 'Configuración del asistente inteligente', tipo: 'ai-settings' },
      { id: 'backups', nombre: 'Copias de Seguridad', icon: HardDrive, descripcion: 'Backup de base de datos y archivos', tipo: 'external-link', externalUrl: 'https://app.lexduo.cat/backup-admin/' },
      { id: 's3-storage', nombre: 'Almacenamiento S3', icon: Cloud, descripcion: 'Gestión de archivos en la nube AWS S3', tipo: 'link', link: '/configuracion/s3' },
    ]
  },
  {
    id: 'privacidad',
    nombre: 'Privacidad y Seguridad',
    opciones: [
      { id: 'privacidad-rgpd', nombre: 'Privacidad y Seguridad RGPD', icon: Shield, descripcion: 'Cumplimiento normativo RGPD/LOPDGDD', tipo: 'link', link: '/configuracion/privacidad-rgpd' },
      { id: 'auditoria-rgpd', nombre: 'Auditoría de Accesos', icon: Eye, descripcion: 'Logs de acceso y trazabilidad', tipo: 'link', link: '/configuracion/auditoria' },
    ]
  },
  {
    id: 'administracion',
    nombre: 'Administración',
    opciones: [
      { id: 'gestion-datos', nombre: 'Gestión de Datos', icon: Database, descripcion: 'Exportar o borrar datos de la base de datos', tipo: 'link', link: '/configuracion/gestion-datos' },
      { id: 'importacion', nombre: 'Importación de Datos', icon: Download, descripcion: 'Importar datos desde otras aplicaciones (Quolaw, etc.)', tipo: 'external-link', externalUrl: 'http://app.lexduo.cat:8080/' },
      { id: 'actualizaciones', nombre: 'Actualizar Sistema', icon: RefreshCw, descripcion: 'Aplicar paquetes de actualización al sistema', tipo: 'link', link: '/configuracion/actualizaciones' },
    ]
  }
];

const MODULOS_INFO = [
  { id: "panel", nombre: "Panel", descripcion: "Dashboard principal" },
  { id: "calendario", nombre: "Calendario", descripcion: "Gestión de eventos y agenda" },
  { id: "calculadora", nombre: "Calculadoras", descripcion: "Cálculo de plazos e intereses" },
  { 
    id: "contactos", 
    nombre: "Contactos", 
    descripcion: "Gestión de contactos y clientes",
    submodulos: [
      { id: "contactos-lista", nombre: "Lista de contactos" },
      { id: "contactos-estadisticas", nombre: "Estadísticas" }
    ]
  },
  { 
    id: "expedientes", 
    nombre: "Expedientes", 
    descripcion: "Gestión de expedientes legales",
    submodulos: [
      { id: "expedientes-lista", nombre: "Lista de expedientes" },
      { id: "expedientes-supervision", nombre: "Supervisión" },
      { id: "expedientes-estadisticas", nombre: "Estadísticas" }
    ]
  },
  { id: "informes", nombre: "Informes", descripcion: "Generación de informes" },
  { 
    id: "contabilidad", 
    nombre: "Contabilidad", 
    descripcion: "Facturación, gastos y morosidad",
    submodulos: [
      { id: "contabilidad-facturas", nombre: "Facturas" },
      { id: "contabilidad-gastos", nombre: "Control de gastos" },
      { id: "contabilidad-morosidad", nombre: "Morosidad" }
    ]
  },
  { 
    id: "tareas", 
    nombre: "Tareas", 
    descripcion: "Gestión de tareas del despacho",
    submodulos: [
      { id: "tareas-lista", nombre: "Lista de tareas" },
      { id: "tareas-estadisticas", nombre: "Estadísticas" }
    ]
  },
  { id: "comunicaciones", nombre: "Comunicaciones", descripcion: "Envío de emails y notificaciones" },
  { id: "chat", nombre: "Asistente IA", descripcion: "Asistente inteligente" },
  { id: "correo", nombre: "Correo", descripcion: "Gestión de correo electrónico" },
  { id: "plantillas", nombre: "Plantillas", descripcion: "Plantillas de documentos" },
  { id: "extrajudicial", nombre: "Extrajudicial", descripcion: "Reclamaciones extrajudiciales" },
  { 
    id: "configuracion", 
    nombre: "Configuración", 
    descripcion: "Configuración del sistema",
    submodulos: [
      { id: "configuracion-general", nombre: "Configuración general" },
      { id: "configuracion-usuarios", nombre: "Gestión de usuarios" },
      { id: "configuracion-empresa", nombre: "Gestión de empresa" },
      { id: "configuracion-backups", nombre: "Copias de seguridad" }
    ]
  }
];

// Widgets del Panel Principal configurables por usuario
const WIDGETS_PANEL = [
  { id: "widget-estadisticas", nombre: "Tarjetas de Estadísticas", descripcion: "Expedientes activos, contactos, facturación" },
  { id: "widget-expedientes-activos", nombre: "Expedientes Activos", descripcion: "Contador de expedientes activos" },
  { id: "widget-contactos", nombre: "Contactos", descripcion: "Contador de contactos totales" },
  { id: "widget-tareas-pendientes", nombre: "Tareas Pendientes", descripcion: "Contador y lista de tareas" },
  { id: "widget-eventos", nombre: "Eventos Próximos", descripcion: "Contador y lista de eventos" },
  { id: "widget-facturacion", nombre: "Facturación", descripcion: "Facturación pendiente y cobrada" },
  { id: "widget-expedientes-recientes", nombre: "Expedientes Recientes", descripcion: "Lista de últimos expedientes" },
  { id: "widget-gastos", nombre: "Control de Gastos", descripcion: "Estadísticas de gastos" },
];

const Configuracion = () => {
  const navigate = useNavigate();
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [selectedOpcion, setSelectedOpcion] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [cargaTrabajo, setCargaTrabajo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPermisosDialogOpen, setIsPermisosDialogOpen] = useState(false);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [isEditItemDialogOpen, setIsEditItemDialogOpen] = useState(false);
  const [editedPermisos, setEditedPermisos] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para configuraciones
  const [configItems, setConfigItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItem, setNewItem] = useState({ nombre: '', descripcion: '', color: '', valor: '' });
  
  // Estados para historial
  const [historialAcciones, setHistorialAcciones] = useState([]);
  
  // Estados para calendarios
  const [calendarios, setCalendarios] = useState([]);
  
  // Estados para facturadores
  const [facturadores, setFacturadores] = useState([]);
  
  // Estados para grupos
  const [gruposUsuarios, setGruposUsuarios] = useState([]);

  useEffect(() => {
    loadData();
    // Inicializar datos de configuración al cargar
    initializeConfigData();
  }, []);

  const initializeConfigData = async () => {
    try {
      await configuracionAPI.inicializarDatos();
    } catch (error) {
      // Ignorar si ya existen datos
    }
  };

  const loadData = async () => {
    try {
      const [usuariosRes, cargaRes] = await Promise.all([
        usuariosAPI.getAll(),
        usuariosAPI.getCargaTrabajo()
      ]);
      setUsuarios(usuariosRes.data);
      setCargaTrabajo(cargaRes.data.estadisticas || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfigItems = useCallback(async (categoria) => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getItems(categoria);
      setConfigItems(response.data);
    } catch (error) {
      console.error('Error cargando items:', error);
      toast({ title: "Error", description: "No se pudieron cargar los datos", variant: "destructive" });
    } finally {
      setLoadingItems(false);
    }
  }, []);

  const loadHistorial = async () => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getHistorialAcciones({ limit: 100 });
      setHistorialAcciones(response.data);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadCalendarios = async () => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getCalendarios();
      setCalendarios(response.data);
    } catch (error) {
      console.error('Error cargando calendarios:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadFacturadores = async () => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getFacturadores();
      setFacturadores(response.data);
    } catch (error) {
      console.error('Error cargando facturadores:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadGruposUsuarios = async () => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getGruposUsuarios();
      setGruposUsuarios(response.data);
    } catch (error) {
      console.error('Error cargando grupos:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  // Estado y funciones para Accesos Rápidos
  const [accesosRapidos, setAccesosRapidos] = useState([]);
  
  const ACCESOS_DISPONIBLES = [
    { id: 'nuevo-expediente', nombre: 'Nuevo Expediente', icon: 'Briefcase', ruta: '/expedientes/nuevo' },
    { id: 'nuevo-contacto', nombre: 'Nuevo Contacto', icon: 'User', ruta: '/contactos/nuevo' },
    { id: 'nueva-factura', nombre: 'Nueva Factura', icon: 'Receipt', ruta: '/facturas/nueva' },
    { id: 'nuevo-evento', nombre: 'Nuevo Evento', icon: 'Calendar', ruta: '/calendario/nuevo' },
    { id: 'nueva-tarea', nombre: 'Nueva Tarea', icon: 'Check', ruta: '/tareas/nueva' },
    { id: 'nueva-comunicacion', nombre: 'Nueva Comunicación', icon: 'Mail', ruta: '/comunicaciones/nueva' },
    { id: 'nueva-actuacion', nombre: 'Nueva Actuación', icon: 'FileText', ruta: '/actuaciones/nueva' },
    { id: 'nuevo-gasto', nombre: 'Nuevo Gasto', icon: 'DollarSign', ruta: '/gastos/nuevo' },
  ];

  const loadAccesosRapidos = async () => {
    setLoadingItems(true);
    try {
      const response = await configuracionAPI.getItems('accesos-rapidos');
      setAccesosRapidos(response.data || []);
    } catch (error) {
      // Si no existe la categoría, inicializar con valores por defecto
      setAccesosRapidos([
        { id: 'nuevo-expediente', activo: true, orden: 0 },
        { id: 'nuevo-contacto', activo: true, orden: 1 },
        { id: 'nueva-factura', activo: true, orden: 2 },
      ]);
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleAccesoRapido = async (accesoId) => {
    const existe = accesosRapidos.find(a => a.id === accesoId);
    let nuevosAccesos;
    
    if (existe) {
      nuevosAccesos = accesosRapidos.map(a => 
        a.id === accesoId ? { ...a, activo: !a.activo } : a
      );
    } else {
      nuevosAccesos = [...accesosRapidos, { id: accesoId, activo: true, orden: accesosRapidos.length }];
    }
    
    setAccesosRapidos(nuevosAccesos);
    
    // Guardar en configuración
    try {
      await configuracionAPI.createItem({
        categoria: 'accesos-rapidos',
        nombre: 'config',
        valor: JSON.stringify(nuevosAccesos)
      });
      toast({ title: "Accesos rápidos actualizados" });
    } catch (error) {
      console.error('Error guardando accesos rápidos:', error);
    }
  };

  const handleCreateUsuario = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await usuariosAPI.create({
        email: formData.get('email'),
        nombre: formData.get('nombre'),
        password: formData.get('password'),
        rol: formData.get('rol') || 'abogado'
      });
      toast({ title: "Usuario creado", description: "El usuario se ha creado correctamente" });
      setIsCreateDialogOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo crear el usuario", variant: "destructive" });
    }
  };

  const handleUpdateUsuario = async (e) => {
    e.preventDefault();
    if (!selectedUsuario) return;
    const formData = new FormData(e.target);
    const newPassword = formData.get('new_password');
    
    try {
      // Actualizar datos básicos
      await usuariosAPI.update(selectedUsuario.id, {
        email: formData.get('email'),
        nombre: formData.get('nombre'),
        rol: formData.get('rol'),
        activo: formData.get('activo') === 'on'
      });
      
      // Si se proporcionó nueva contraseña, cambiarla
      if (newPassword && newPassword.trim()) {
        await usuariosAPI.cambiarPassword(selectedUsuario.id, newPassword);
        toast({ title: "Usuario y contraseña actualizados" });
      } else {
        toast({ title: "Usuario actualizado" });
      }
      
      setIsEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo actualizar", variant: "destructive" });
    }
  };

  const openPermisosDialog = (usuario) => {
    setSelectedUsuario(usuario);
    const permisos = {};
    MODULOS_INFO.forEach(m => {
      const permisoModulo = usuario.permisos?.[m.id];
      if (typeof permisoModulo === 'object') {
        permisos[m.id] = { ...permisoModulo };
      } else {
        permisos[m.id] = { lectura: !!permisoModulo, escritura: !!permisoModulo };
      }
      // Cargar permisos de submodulos
      if (m.submodulos) {
        m.submodulos.forEach(sub => {
          const permisoSub = usuario.permisos?.[sub.id];
          if (typeof permisoSub === 'object') {
            permisos[sub.id] = { ...permisoSub };
          } else {
            permisos[sub.id] = { lectura: !!permisoSub, escritura: !!permisoSub };
          }
        });
      }
    });
    
    // Cargar permisos de widgets del panel
    WIDGETS_PANEL.forEach(widget => {
      const permisoWidget = usuario.permisos?.[widget.id];
      // Por defecto, todos los widgets están visibles (lectura: true)
      if (typeof permisoWidget === 'object') {
        permisos[widget.id] = { ...permisoWidget };
      } else if (permisoWidget === false) {
        permisos[widget.id] = { lectura: false, escritura: false };
      } else {
        permisos[widget.id] = { lectura: true, escritura: true };
      }
    });
    
    setEditedPermisos(permisos);
    setIsPermisosDialogOpen(true);
  };

  const togglePermiso = (modulo, tipo) => {
    setEditedPermisos(prev => ({
      ...prev,
      [modulo]: { ...prev[modulo], [tipo]: !prev[modulo]?.[tipo] }
    }));
  };

  const toggleModuloCompleto = (modulo, checked) => {
    setEditedPermisos(prev => {
      const newPermisos = { ...prev };
      // Activar/desactivar módulo principal
      newPermisos[modulo.id] = { lectura: checked, escritura: checked };
      // Activar/desactivar todos los submodulos
      if (modulo.submodulos) {
        modulo.submodulos.forEach(sub => {
          newPermisos[sub.id] = { lectura: checked, escritura: checked };
        });
      }
      return newPermisos;
    });
  };

  const handleSavePermisos = async () => {
    if (!selectedUsuario) return;
    try {
      await usuariosAPI.updatePermisos(selectedUsuario.id, editedPermisos);
      toast({ title: "Permisos actualizados" });
      setIsPermisosDialogOpen(false);
      loadData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleToggleActivo = async (usuario) => {
    try {
      await usuariosAPI.update(usuario.id, { activo: !usuario.activo });
      toast({ title: usuario.activo ? "Usuario desactivado" : "Usuario activado" });
      loadData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteUsuario = async (usuario) => {
    if (usuario.rol === 'admin') {
      toast({ 
        title: "No permitido", 
        description: "No se puede eliminar un usuario administrador", 
        variant: "destructive" 
      });
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${usuario.nombre}"?\n\nEsta acción es irreversible y se eliminarán todos los datos asociados.`)) {
      return;
    }
    
    try {
      await usuariosAPI.delete(usuario.id);
      toast({ title: "Usuario eliminado correctamente" });
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al eliminar el usuario";
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    }
  };

  const openConfigOption = async (opcion) => {
    // Si es un link externo, abrir en nueva ventana
    if (opcion.tipo === 'external-link' && opcion.externalUrl) {
      window.open(opcion.externalUrl, '_blank');
      return;
    }
    
    // Si es un link interno, navegar directamente
    if (opcion.tipo === 'link' && opcion.link) {
      navigate(opcion.link);
      return;
    }
    
    setSelectedOpcion(opcion);
    setNewItem({ nombre: '', descripcion: '', color: '', valor: '' });
    
    if (opcion.tipo === 'items') {
      await loadConfigItems(opcion.id);
      setIsConfigDialogOpen(true);
    } else if (opcion.tipo === 'historial') {
      await loadHistorial();
      setSelectedCategoria({ id: 'historial-detalle', nombre: opcion.nombre });
    } else if (opcion.tipo === 'calendarios') {
      await loadCalendarios();
      setIsConfigDialogOpen(true);
    } else if (opcion.tipo === 'facturadores') {
      await loadFacturadores();
      setIsConfigDialogOpen(true);
    } else if (opcion.tipo === 'grupos') {
      await loadGruposUsuarios();
      setIsConfigDialogOpen(true);
    } else if (opcion.tipo === 'usuarios') {
      setSelectedCategoria({ id: 'usuarios-detalle' });
    } else if (opcion.tipo === 'ai-settings') {
      setSelectedCategoria({ id: 'ai-settings-detalle', nombre: opcion.nombre });
    } else if (opcion.tipo === 'ai-expedientes') {
      setSelectedCategoria({ id: 'ai-expedientes-detalle', nombre: opcion.nombre });
    } else if (opcion.tipo === 'empresa') {
      navigate('/configuracion/empresa');
    } else if (opcion.tipo === 'accesos-rapidos') {
      await loadAccesosRapidos();
      setSelectedCategoria({ id: 'accesos-rapidos-detalle', nombre: opcion.nombre });
    } else {
      setIsConfigDialogOpen(true);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es requerido", variant: "destructive" });
      return;
    }
    
    try {
      await configuracionAPI.createItem({
        categoria: selectedOpcion.id,
        nombre: newItem.nombre,
        descripcion: newItem.descripcion || null,
        color: newItem.color || null,
        valor: newItem.valor || null,
        orden: configItems.length
      });
      toast({ title: "Elemento añadido" });
      setNewItem({ nombre: '', descripcion: '', color: '', valor: '' });
      await loadConfigItems(selectedOpcion.id);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear el elemento", variant: "destructive" });
    }
  };

  const handleUpdateItem = async () => {
    if (!selectedItem) return;
    try {
      await configuracionAPI.updateItem(selectedItem.id, {
        nombre: selectedItem.nombre,
        descripcion: selectedItem.descripcion,
        color: selectedItem.color,
        valor: selectedItem.valor
      });
      toast({ title: "Elemento actualizado" });
      setIsEditItemDialogOpen(false);
      await loadConfigItems(selectedOpcion.id);
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await configuracionAPI.deleteItem(itemId);
      toast({ title: "Elemento eliminado" });
      await loadConfigItems(selectedOpcion.id);
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleAddCalendario = async () => {
    if (!newItem.nombre.trim()) return;
    try {
      await configuracionAPI.createCalendario({
        nombre: newItem.nombre,
        color: newItem.color || '#3B82F6',
        descripcion: newItem.descripcion
      });
      toast({ title: "Calendario creado" });
      setNewItem({ nombre: '', descripcion: '', color: '', valor: '' });
      await loadCalendarios();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteCalendario = async (id) => {
    try {
      await configuracionAPI.deleteCalendario(id);
      toast({ title: "Calendario eliminado" });
      await loadCalendarios();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleAddGrupo = async () => {
    if (!newItem.nombre.trim()) return;
    try {
      await configuracionAPI.createGrupoUsuario({
        nombre: newItem.nombre,
        descripcion: newItem.descripcion
      });
      toast({ title: "Grupo creado" });
      setNewItem({ nombre: '', descripcion: '', color: '', valor: '' });
      await loadGruposUsuarios();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const getRolBadge = (rol) => {
    const colors = { admin: 'bg-red-100 text-red-700', abogado: 'bg-blue-100 text-blue-700', asistente: 'bg-green-100 text-green-700' };
    return <Badge className={colors[rol] || 'bg-gray-100 text-gray-700'}>{rol}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-gray-600">Cargando configuración...</span>
        </div>
      </div>
    );
  }

  // Vista de configuración del Asistente IA
  if (selectedCategoria?.id === 'ai-settings-detalle') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedCategoria(null)}>
            <ChevronLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuración del Asistente IA</h1>
            <p className="text-gray-600">Configura el proveedor y comportamiento del asistente inteligente</p>
          </div>
        </div>
        <AISettings />
      </div>
    );
  }

  // Vista de configuración de IA para Expedientes
  if (selectedCategoria?.id === 'ai-expedientes-detalle') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedCategoria(null)}>
            <ChevronLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuración de IA para Expedientes</h1>
            <p className="text-gray-600">Configura el proveedor de IA para análisis de expedientes, resúmenes y estrategias</p>
          </div>
        </div>
        <AIExpedienteSettings />
      </div>
    );
  }

  // Vista de configuración de Accesos Rápidos
  if (selectedCategoria?.id === 'accesos-rapidos-detalle') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedCategoria(null)}>
            <ChevronLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accesos Rápidos</h1>
            <p className="text-gray-600">Configura los accesos directos del botón "+" en la esquina inferior del módulo de expedientes</p>
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Acciones Disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingItems ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 mb-4">
                  Activa o desactiva las acciones que aparecerán en el menú de acceso rápido:
                </p>
                {ACCESOS_DISPONIBLES.map((acceso) => {
                  const config = accesosRapidos.find(a => a.id === acceso.id);
                  const isActivo = config?.activo ?? false;
                  
                  return (
                    <div 
                      key={acceso.id}
                      onClick={() => toggleAccesoRapido(acceso.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                        isActivo ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isActivo ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {acceso.icon === 'Briefcase' && <Briefcase className="w-5 h-5" />}
                          {acceso.icon === 'User' && <User className="w-5 h-5" />}
                          {acceso.icon === 'Receipt' && <Receipt className="w-5 h-5" />}
                          {acceso.icon === 'Calendar' && <Calendar className="w-5 h-5" />}
                          {acceso.icon === 'Check' && <Check className="w-5 h-5" />}
                          {acceso.icon === 'Mail' && <Mail className="w-5 h-5" />}
                          {acceso.icon === 'FileText' && <FileText className="w-5 h-5" />}
                          {acceso.icon === 'DollarSign' && <DollarSign className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium">{acceso.nombre}</p>
                          <p className="text-xs text-gray-500">{acceso.ruta}</p>
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        isActivo ? 'bg-blue-600' : 'bg-gray-300'
                      } relative`}>
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          isActivo ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vista de historial de acciones
  if (selectedCategoria?.id === 'historial-detalle') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedCategoria(null)}>
            <ChevronLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Historial de Acciones</h1>
            <p className="text-gray-600">Registro de actividad del sistema</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadHistorial} className="ml-auto">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizar
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            {loadingItems ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              </div>
            ) : historialAcciones.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay acciones registradas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entidad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {historialAcciones.map((accion) => (
                      <tr key={accion.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {accion.created_at ? new Date(accion.created_at).toLocaleString('es-ES') : '-'}
                        </td>
                        <td className="px-4 py-3 font-medium">{accion.usuario_nombre || 'Sistema'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{accion.accion}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {accion.entidad} {accion.entidad_id && `#${accion.entidad_id}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {accion.detalles}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vista principal de categorías
  if (!selectedCategoria) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ajustes</h1>
          <p className="text-gray-600 mt-1">Configura tu LEXDUO</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Navegación lateral */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-1">
                {CATEGORIAS_CONFIG.map((cat) => (
                  <div key={cat.id} className="space-y-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 pt-3">
                      {cat.nombre}
                    </p>
                    {cat.opciones.map((opcion) => {
                      const Icon = opcion.icon;
                      return (
                        <button
                          key={opcion.id}
                          onClick={() => openConfigOption(opcion)}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 text-left transition-colors"
                        >
                          <Icon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-700">{opcion.nombre}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Grid de opciones */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {CATEGORIAS_CONFIG.flatMap(cat => 
                cat.opciones.map(opcion => {
                  const Icon = opcion.icon;
                  return (
                    <Card 
                      key={opcion.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                      onClick={() => openConfigOption(opcion)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">{opcion.nombre}</h3>
                              {opcion.tipo === 'external-link' && (
                                <ExternalLink className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{opcion.descripcion}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Diálogo de configuración genérico para items */}
        <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedOpcion && <selectedOpcion.icon className="w-5 h-5 text-blue-600" />}
                {selectedOpcion?.nombre}
              </DialogTitle>
            </DialogHeader>
            
            {selectedOpcion?.tipo === 'items' && (
              <div className="space-y-4">
                {/* Formulario para añadir */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-gray-700">Añadir nuevo elemento</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input 
                      value={newItem.nombre}
                      onChange={(e) => setNewItem({...newItem, nombre: e.target.value})}
                      placeholder="Nombre *"
                    />
                    <Input 
                      value={newItem.descripcion}
                      onChange={(e) => setNewItem({...newItem, descripcion: e.target.value})}
                      placeholder="Descripción (opcional)"
                    />
                    {selectedOpcion.conColor && (
                      <div className="flex gap-2">
                        <Input 
                          type="color"
                          value={newItem.color || '#3B82F6'}
                          onChange={(e) => setNewItem({...newItem, color: e.target.value})}
                          className="w-14 p-1 h-10"
                        />
                        <Input 
                          value={newItem.color}
                          onChange={(e) => setNewItem({...newItem, color: e.target.value})}
                          placeholder="Color (hex)"
                          className="flex-1"
                        />
                      </div>
                    )}
                    {selectedOpcion.conValor && (
                      <Input 
                        value={newItem.valor}
                        onChange={(e) => setNewItem({...newItem, valor: e.target.value})}
                        placeholder="Valor (ej: 21 para 21%)"
                      />
                    )}
                  </div>
                  <Button onClick={handleAddItem} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />Añadir
                  </Button>
                </div>

                {/* Lista de items */}
                <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                  {loadingItems ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
                    </div>
                  ) : configItems.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">No hay elementos configurados</p>
                  ) : (
                    <div className="divide-y">
                      {configItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            {item.color && (
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-200" 
                                style={{ backgroundColor: item.color }}
                              />
                            )}
                            <div>
                              <span className="font-medium">{item.nombre}</span>
                              {item.descripcion && (
                                <p className="text-xs text-gray-500">{item.descripcion}</p>
                              )}
                              {item.valor && (
                                <Badge variant="outline" className="text-xs mt-1">{item.valor}%</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedItem({...item}); setIsEditItemDialogOpen(true); }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteItem(item.id)} 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedOpcion?.tipo === 'calendarios' && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-gray-700">Crear calendario</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input 
                      value={newItem.nombre}
                      onChange={(e) => setNewItem({...newItem, nombre: e.target.value})}
                      placeholder="Nombre del calendario *"
                    />
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={newItem.color || '#3B82F6'}
                        onChange={(e) => setNewItem({...newItem, color: e.target.value})}
                        className="w-14 p-1 h-10"
                      />
                      <Input 
                        value={newItem.descripcion}
                        onChange={(e) => setNewItem({...newItem, descripcion: e.target.value})}
                        placeholder="Descripción"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddCalendario} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />Crear Calendario
                  </Button>
                </div>
                <div className="border rounded-lg">
                  {calendarios.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">No hay calendarios</p>
                  ) : (
                    <div className="divide-y">
                      {calendarios.map((cal) => (
                        <div key={cal.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: cal.color }}
                            />
                            <div>
                              <span className="font-medium">{cal.nombre}</span>
                              {cal.descripcion && <p className="text-xs text-gray-500">{cal.descripcion}</p>}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteCalendario(cal.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedOpcion?.tipo === 'grupos' && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-gray-700">Crear grupo de usuarios</p>
                  <Input 
                    value={newItem.nombre}
                    onChange={(e) => setNewItem({...newItem, nombre: e.target.value})}
                    placeholder="Nombre del grupo *"
                  />
                  <Textarea 
                    value={newItem.descripcion}
                    onChange={(e) => setNewItem({...newItem, descripcion: e.target.value})}
                    placeholder="Descripción del grupo"
                    rows={2}
                  />
                  <Button onClick={handleAddGrupo} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />Crear Grupo
                  </Button>
                </div>
                <div className="border rounded-lg">
                  {gruposUsuarios.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">No hay grupos</p>
                  ) : (
                    <div className="divide-y">
                      {gruposUsuarios.map((grupo) => (
                        <div key={grupo.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                          <div>
                            <span className="font-medium">{grupo.nombre}</span>
                            {grupo.descripcion && <p className="text-xs text-gray-500">{grupo.descripcion}</p>}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => configuracionAPI.deleteGrupoUsuario(grupo.id).then(loadGruposUsuarios)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedOpcion?.tipo === 'facturadores' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Gestión de facturadores del despacho</p>
                <div className="border rounded-lg">
                  {facturadores.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">No hay facturadores configurados</p>
                  ) : (
                    <div className="divide-y">
                      {facturadores.map((fact) => (
                        <div key={fact.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                          <div>
                            <span className="font-medium">{fact.nombre}</span>
                            {fact.nif && <Badge variant="outline" className="ml-2">{fact.nif}</Badge>}
                            {fact.email && <p className="text-xs text-gray-500">{fact.email}</p>}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => configuracionAPI.deleteFacturador(fact.id).then(loadFacturadores)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo de edición de item */}
        <Dialog open={isEditItemDialogOpen} onOpenChange={setIsEditItemDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar elemento</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input 
                    value={selectedItem.nombre}
                    onChange={(e) => setSelectedItem({...selectedItem, nombre: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Input 
                    value={selectedItem.descripcion || ''}
                    onChange={(e) => setSelectedItem({...selectedItem, descripcion: e.target.value})}
                  />
                </div>
                {selectedOpcion?.conColor && (
                  <div>
                    <Label>Color</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={selectedItem.color || '#3B82F6'}
                        onChange={(e) => setSelectedItem({...selectedItem, color: e.target.value})}
                        className="w-14 p-1 h-10"
                      />
                      <Input 
                        value={selectedItem.color || ''}
                        onChange={(e) => setSelectedItem({...selectedItem, color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                )}
                {selectedOpcion?.conValor && (
                  <div>
                    <Label>Valor</Label>
                    <Input 
                      value={selectedItem.valor || ''}
                      onChange={(e) => setSelectedItem({...selectedItem, valor: e.target.value})}
                    />
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditItemDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleUpdateItem}>
                <Save className="w-4 h-4 mr-2" />Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Vista de detalle de usuarios
  if (selectedCategoria.id === 'usuarios-detalle') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedCategoria(null)}>
            <ChevronLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-gray-600">Administra los usuarios y sus permisos</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Usuarios</p>
                  <p className="text-2xl font-bold">{usuarios.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Usuarios Activos</p>
                  <p className="text-2xl font-bold">{usuarios.filter(u => u.activo).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Administradores</p>
                  <p className="text-2xl font-bold">{usuarios.filter(u => u.rol === 'admin').length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Lista de Usuarios</CardTitle>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />Nuevo Usuario
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permisos</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="font-medium">{usuario.nombre}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{usuario.email}</td>
                      <td className="px-4 py-3">{getRolBadge(usuario.rol)}</td>
                      <td className="px-4 py-3">
                        <Badge className={usuario.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {usuario.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="outline" size="sm" onClick={() => openPermisosDialog(usuario)}>
                          <Shield className="w-4 h-4 mr-1" />Gestionar
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedUsuario(usuario); setIsEditDialogOpen(true); }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActivo(usuario)} className={usuario.activo ? "text-yellow-600" : "text-green-600"} title={usuario.activo ? "Desactivar usuario" : "Activar usuario"}>
                            {usuario.activo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteUsuario(usuario)} 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar usuario"
                            disabled={usuario.rol === 'admin'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {cargaTrabajo.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estadísticas de Carga de Trabajo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Expedientes Creados</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actuaciones</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Exp. Activos</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Carga Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cargaTrabajo.map((stat) => (
                      <tr key={stat.usuario_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{stat.nombre}</td>
                        <td className="px-4 py-3 text-center"><span className="text-lg font-bold text-blue-600">{stat.expedientes_creados}</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-lg font-bold text-green-600">{stat.actuaciones_creadas}</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-lg font-bold text-purple-600">{stat.expedientes_activos}</span></td>
                        <td className="px-4 py-3 text-center"><Badge className="bg-orange-100 text-orange-700 text-lg px-3">{stat.carga_total}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Diálogos de usuarios */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nuevo Usuario</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateUsuario} className="space-y-4">
              <div><Label>Nombre *</Label><Input name="nombre" required /></div>
              <div><Label>Email *</Label><Input name="email" type="email" required /></div>
              <div>
                <Label>Contraseña *</Label>
                <div className="relative">
                  <Input name="password" type={showPassword ? "text" : "password"} required />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Rol *</Label>
                <Select name="rol" defaultValue="abogado">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="abogado">Abogado</SelectItem>
                    <SelectItem value="asistente">Asistente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
                <Button type="submit">Crear</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Editar Usuario</DialogTitle></DialogHeader>
            {selectedUsuario && (
              <form onSubmit={handleUpdateUsuario} className="space-y-4">
                <div><Label>Nombre *</Label><Input name="nombre" defaultValue={selectedUsuario.nombre} required /></div>
                <div><Label>Email *</Label><Input name="email" type="email" defaultValue={selectedUsuario.email} required /></div>
                <div>
                  <Label>Rol *</Label>
                  <Select name="rol" defaultValue={selectedUsuario.rol}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="abogado">Abogado</SelectItem>
                      <SelectItem value="asistente">Asistente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nueva contraseña (dejar en blanco para no cambiar)</Label>
                  <div className="relative">
                    <Input name="new_password" type={showPassword ? "text" : "password"} placeholder="••••••••" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="edit-activo" name="activo" defaultChecked={selectedUsuario.activo} className="w-4 h-4" />
                  <Label htmlFor="edit-activo">Usuario activo</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit">Guardar</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={isPermisosDialogOpen} onOpenChange={setIsPermisosDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />Permisos de {selectedUsuario?.nombre}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p><strong>Lectura:</strong> Ver y consultar</p>
                <p><strong>Escritura:</strong> Crear, editar y eliminar</p>
              </div>
              
              {/* Sección: Módulos */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Módulos del Sistema
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Módulo / Submenú</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Lectura</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Escritura</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-20">Todo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {MODULOS_INFO.map((modulo) => (
                        <React.Fragment key={modulo.id}>
                          {/* Módulo principal */}
                          <tr className="bg-gray-50 hover:bg-gray-100">
                            <td className="px-4 py-3">
                              <p className="font-semibold text-gray-900">{modulo.nombre}</p>
                              <p className="text-xs text-gray-500">{modulo.descripcion}</p>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button type="button" onClick={() => togglePermiso(modulo.id, 'lectura')}
                                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${editedPermisos[modulo.id]?.lectura ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-400'}`}>
                                {editedPermisos[modulo.id]?.lectura ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button type="button" onClick={() => togglePermiso(modulo.id, 'escritura')}
                                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${editedPermisos[modulo.id]?.escritura ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-400'}`}>
                                {editedPermisos[modulo.id]?.escritura ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {modulo.submodulos && (
                                <button 
                                  type="button" 
                                  onClick={() => toggleModuloCompleto(modulo, !editedPermisos[modulo.id]?.lectura)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {editedPermisos[modulo.id]?.lectura ? 'Quitar' : 'Activar'}
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Submenús */}
                          {modulo.submodulos && modulo.submodulos.map((sub) => (
                            <tr key={sub.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 pl-10">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">{sub.nombre}</span>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button type="button" onClick={() => togglePermiso(sub.id, 'lectura')}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${editedPermisos[sub.id]?.lectura ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                  {editedPermisos[sub.id]?.lectura ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button type="button" onClick={() => togglePermiso(sub.id, 'escritura')}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${editedPermisos[sub.id]?.escritura ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                  {editedPermisos[sub.id]?.escritura ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                </button>
                              </td>
                              <td></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sección: Widgets del Panel */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Widgets del Panel Principal
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  Selecciona qué información puede ver este usuario en el panel principal
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-purple-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-purple-700 uppercase">Widget</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-purple-700 uppercase w-24">Visible</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {WIDGETS_PANEL.map((widget) => (
                        <tr key={widget.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{widget.nombre}</p>
                            <p className="text-xs text-gray-500">{widget.descripcion}</p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button 
                              type="button" 
                              onClick={() => togglePermiso(widget.id, 'lectura')}
                              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${editedPermisos[widget.id]?.lectura ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-400'}`}
                            >
                              {editedPermisos[widget.id]?.lectura ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPermisosDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSavePermisos}><Save className="w-4 h-4 mr-2" />Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

export default Configuracion;
